import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import merge from 'lodash/merge'

import { COLORS, FONTS, THEMES } from '@constants/theme'

import { darkColors, lightColors } from './colors'
import themePalette from './palette'
import SHADOWS from './shadows'
import WORKFORCE_COLORS from './workforce.colors'

const baseOptions = {
  direction: 'ltr',
  sidebar: {
    width: {
      xs: '100%',
      md: 208,
    },
    collapsedWidth: {
      xs: '100%',
      md: 70,
    },
    iconColor: '#F0F0F0',
  },
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgb(97, 97, 97)',
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 500,
      fontSize: '.75rem',
      hover: '#EDBD00',
    },
    fontFamily: FONTS.PRIMARY_TEXT,
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h3: {
      fontWeight: 500,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '.75rem',
    },
    overline: {
      fontWeight: 500,
    },
    body1: {
      fontSize: '.75rem',
      fontWeight: 500,
    },
    body2: {
      fontSize: '.75rem',
      fontWeight: 700,
    },
    caption: {
      fontSize: '.75rem',
      fontWeight: 500,
      fontStyle: 'italic',
    },
    subsectionHeading: {
      display: 'block',
      fontWeight: 700,
      fontSize: '1rem',
      borderBottom: '1px solid #979CA4',
    },
    subtitle1: {
      fontWeight: 700,
      fontSize: '.75rem',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '.75rem',
    },
    textColor: {
      primary: '#F1F2F3',
      secondary: '#979CA4',
      richBlack: '#141B29',
      basicWhite: '#FFFFFF',
    },
  },
  fonts: {
    primary: FONTS.CIRCULAR_STD_MEDIUM,
    secondary: FONTS.INTER,
  },
  colors: {
    primary: '#F1F2F3',
    brightLink: '#94C7F0',
    info: '#3267E3',
    success: '#10DC78',
    danger: '#F15950',
    richBlack: '#141B29',
    neutral: COLORS.WHITE,
    slate: '#323F4B',
    yellow: '#DDB900',
    white: '#F1F2F3',
    green: '#25AA61',
    error: '#E14243',
    primaryBlack: '#000',
  },
}

const baseThemeVariants = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: COLORS.SLATE_BLUE,
            },
          },
        },
      },
    },
    palette: {
      action: {
        active: COLORS.SLATE_GRAY,
      },
      background: {
        default: COLORS.LIGHT_GRAY,
        paper: COLORS.WHITE,
      },
      border: {
        default: 'rgba(0, 0, 0, 0.12)',
      },
      error: {
        contrastText: COLORS.WHITE,
        main: COLORS.RED,
      },
      mode: 'light',
      primary: {
        contrastText: COLORS.WHITE,
        main: COLORS.PURPLE,
      },
      success: {
        contrastText: COLORS.WHITE,
        main: COLORS.GREEN,
      },
      text: {
        primary: COLORS.DEEP_OCEAN,
        secondary: COLORS.SLATE_GRAY,
      },
      link: {
        primary: COLORS.LIGHT_BLUE,
        secondary: COLORS.SLATE_GRAY,
      },
      warning: {
        contrastText: COLORS.WHITE,
        main: COLORS.ORANGE,
      },
    },
    shadows: SHADOWS.light,
  },
  [THEMES.DARK]: {
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: COLORS.CHARCOAL_GRAY,
            border: '1px solid rgba(145, 158, 171, 0.24)',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #161616 inset!important',
            },
          },
        },
      },
    },
    palette: {
      background: {
        default: COLORS.CHARCOAL,
        paper: COLORS.JET_BLACK,
      },
      divider: COLORS.FADED_STEEL_BLUE,
      error: {
        contrastText: COLORS.WHITE,
        main: COLORS.RED,
      },
      mode: 'dark',
      primary: {
        contrastText: COLORS.NERO,
        main: COLORS.BRIGHT_YELLOW,
      },
      success: {
        contrastText: COLORS.WHITE,
        main: COLORS.GREEN,
      },
      text: {
        primary: COLORS.WHITE,
        secondary: COLORS.SLATE_BLUE,
      },
      link: {
        primary: COLORS.LIGHT_BLUE,
        secondary: COLORS.SLATE_GRAY,
      },
      warning: {
        contrastText: COLORS.WHITE,
        main: COLORS.ORANGE,
      },
      hover: {
        main: COLORS.DIM_YELLOW,
      },
    },
    shadows: SHADOWS.dark,
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
          },
        },
      },
    },
    palette: {
      background: {
        default: '#1c2531',
        paper: '#293142',
      },
      divider: COLORS.FADED_STEEL_BLUE,
      error: {
        contrastText: COLORS.WHITE,
        main: COLORS.RED,
      },
      mode: 'dark',
      primary: {
        contrastText: COLORS.WHITE,
        main: COLORS.JUNGLE_GREEN,
      },
      success: {
        contrastText: COLORS.WHITE,
        main: COLORS.GREEN,
      },
      text: {
        primary: COLORS.WHITE,
        secondary: COLORS.SLATE_BLUE,
      },
      link: {
        primary: COLORS.LIGHT_BLUE,
        secondary: COLORS.SLATE_GRAY,
      },
      warning: {
        contrastText: COLORS.WHITE,
        main: COLORS.ORANGE,
      },
    },
    shadows: SHADOWS.dark,
  },
}

export const createCustomTheme = (config: Record<string, any> = {}, customization: Record<string, any> = {}) => {
  let themeOptions = baseThemeVariants[config.theme]

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`))
    themeOptions = baseThemeVariants[THEMES.LIGHT]
  }

  let theme = createTheme(
    merge(
      {},
      { ...baseOptions, ...customization },
      themeOptions,
      {
        ...(config.roundedCorners && {
          shape: {
            borderRadius: 16,
          },
        }),
      },
      {
        direction: config.direction,
      }
    )
  )

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme)
  }

  return theme
}

export const themesOptions = (customization: Record<string, any>) => ({
  [THEMES.LIGHT]: {
    palette: themePalette({
      // Not actually supported/implemented
      colors: lightColors as unknown as typeof darkColors,
      customization,
    }),
    colors: WORKFORCE_COLORS,
  },
  [THEMES.DARK]: {
    palette: themePalette({
      colors: darkColors,
      customization,
    }),
    colors: WORKFORCE_COLORS,
  },
})
